import { useState } from 'react';
import Grid from '@mui/material/Grid';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Chip, 
  Button, 
  Collapse,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import type { NutritionStats } from '../services/nutritionService';

interface NutritionStatsProps {
  stats: NutritionStats & { startDate?: string };
}

export default function NutritionStats({ stats }: NutritionStatsProps) {
  const [expanded, setExpanded] = useState(false);

  const { 
    period, 
    totalEntries, 
    totalFoods, 
    uniqueFoods, 
    foodCounts, 
    nutritionTotals,
    startDate
  } = stats;

  // Formatear fecha de inicio si existe
  let periodLabel = `Estadísticas de ${period}`;
  if (startDate) {
    const date = new Date(startDate);
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' };
    const formatted = date.toLocaleDateString('es-ES', options);
    periodLabel += ` (desde ${formatted})`;
  }

  const topFoods = Object.entries(foodCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5);

  return (
    <Card sx={{ mb: 2, borderRadius: 4, border: '2px solid', borderColor: 'secondary.main', boxShadow: 'none' }}>
      <CardContent>
        <Typography variant="h6" fontWeight={600} color="primary.main" mb={2}>
          {periodLabel}
        </Typography>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Comidas registradas
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {totalEntries}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Alimentos consumidos
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {totalFoods}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Alimentos únicos
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {uniqueFoods.length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">
              Calorías totales
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {nutritionTotals.calories.toFixed(0)}
            </Typography>
          </Grid>
        </Grid>

        <Box mb={2}>
          <Typography variant="body1" fontWeight={500} color="primary.main" mb={1}>
            Alimentos más consumidos
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {topFoods.map(([food, count]) => (
              <Chip 
                key={food}
                label={`${food} (${count})`} 
                color="secondary" 
                variant="outlined"
                sx={{ borderRadius: 2 }} 
              />
            ))}
          </Box>
        </Box>

        <Button 
          variant="text" 
          onClick={() => setExpanded(!expanded)}
          sx={{ mb: 1 }}
        >
          {expanded ? 'Ocultar detalles' : 'Ver detalles nutricionales'}
        </Button>

        <Collapse in={expanded}>
          <Box>
            <Typography variant="body1" fontWeight={500} color="primary.main" mb={1}>
              Macronutrientes
            </Typography>
            <Grid container spacing={2} mb={2}>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">
                  Proteínas
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {nutritionTotals.protein.toFixed(1)}g
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">
                  Carbohidratos
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {nutritionTotals.carbs.toFixed(1)}g
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="body2" color="text.secondary">
                  Grasas
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {nutritionTotals.fat.toFixed(1)}g
                </Typography>
              </Grid>
            </Grid>

            {Object.keys(nutritionTotals.vitamins).length > 0 && (
              <Box mb={2}>
                <Typography variant="body1" fontWeight={500} color="primary.main" mb={1}>
                  Vitaminas consumidas
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {Object.entries(nutritionTotals.vitamins).map(([vitamin, amount]) => (
                    <Chip 
                      key={vitamin}
                      label={`${vitamin}: ${amount.toFixed(1)}`} 
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 2 }} 
                    />
                  ))}
                </Box>
              </Box>
            )}

            {Object.keys(nutritionTotals.minerals).length > 0 && (
              <Box>
                <Typography variant="body1" fontWeight={500} color="primary.main" mb={1}>
                  Minerales consumidos
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {Object.entries(nutritionTotals.minerals).map(([mineral, amount]) => (
                    <Chip 
                      key={mineral}
                      label={`${mineral}: ${amount.toFixed(1)}`} 
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 2 }} 
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
} 